// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MOCK_SOPS } from '../mockData';
import type { SafetyBoundary, EquipmentConfig } from '../../../types';
import './SOPEditor.css';

const ACCEPTED_FILE_EXTENSIONS = '.pdf,.docx,.doc,.txt,.md';
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
  'text/markdown',
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function SOPEditor() {
  const { site_id, sopId } = useParams();
  const isEditing = Boolean(sopId);

  const [name, setName] = useState('');
  const [overview, setOverview] = useState('');
  const [changeReason, setChangeReason] = useState('');
  const [safetyBoundaries, setSafetyBoundaries] = useState<SafetyBoundary[]>([]);
  const [equipmentConfig, setEquipmentConfig] = useState<EquipmentConfig[]>([]);
  const [preChecks, setPreChecks] = useState<string[]>([]);
  const [postChecks, setPostChecks] = useState<string[]>([]);
  const [rollbackInstructions, setRollbackInstructions] = useState<string[]>([]);

  // Document upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sop = isEditing ? (MOCK_SOPS.find(s => s.id === sopId) ?? MOCK_SOPS[0]) : null;

  useEffect(() => {
    if (isEditing && sop) {
      setName(sop.name);
      setOverview(sop.overview);
      setSafetyBoundaries([...sop.safetyBoundaries]);
      setEquipmentConfig([...sop.equipmentConfig]);
      setPreChecks([...sop.preChecks]);
      setPostChecks([...sop.postChecks]);
      setRollbackInstructions([...sop.rollbackInstructions]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // --- Document Upload handlers ---
  const handleFileSelect = useCallback((file: File) => {
    if (ACCEPTED_MIME_TYPES.includes(file.type) || file.name.match(/\.(pdf|docx?|txt|md)$/i)) {
      setUploadedFile(file);
      console.log('Document uploaded:', file.name, file.size, file.type);
    }
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // --- Safety Boundaries helpers ---
  function addBoundaryRow() {
    setSafetyBoundaries(prev => [...prev, { parameter: '', min: '', max: '', unit: '' }]);
  }

  function updateBoundaryRow(index: number, field: keyof SafetyBoundary, value: string) {
    setSafetyBoundaries(prev =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  }

  function removeBoundaryRow(index: number) {
    setSafetyBoundaries(prev => prev.filter((_, i) => i !== index));
  }

  // --- Equipment Config helpers ---
  function addEquipmentRow() {
    setEquipmentConfig(prev => [
      ...prev,
      { equipment: '', parameter: '', normal: '', optimized: '' },
    ]);
  }

  function updateEquipmentRow(index: number, field: keyof EquipmentConfig, value: string) {
    setEquipmentConfig(prev =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  }

  function removeEquipmentRow(index: number) {
    setEquipmentConfig(prev => prev.filter((_, i) => i !== index));
  }

  // --- Checks helpers ---
  function addCheck(type: 'pre' | 'post') {
    if (type === 'pre') {
      setPreChecks(prev => [...prev, '']);
    } else {
      setPostChecks(prev => [...prev, '']);
    }
  }

  function updateCheck(type: 'pre' | 'post', index: number, value: string) {
    if (type === 'pre') {
      setPreChecks(prev => prev.map((c, i) => (i === index ? value : c)));
    } else {
      setPostChecks(prev => prev.map((c, i) => (i === index ? value : c)));
    }
  }

  function removeCheck(type: 'pre' | 'post', index: number) {
    if (type === 'pre') {
      setPreChecks(prev => prev.filter((_, i) => i !== index));
    } else {
      setPostChecks(prev => prev.filter((_, i) => i !== index));
    }
  }

  // --- Rollback helpers ---
  function addRollbackStep() {
    setRollbackInstructions(prev => [...prev, '']);
  }

  function updateRollbackStep(index: number, value: string) {
    setRollbackInstructions(prev => prev.map((s, i) => (i === index ? value : s)));
  }

  function removeRollbackStep(index: number) {
    setRollbackInstructions(prev => prev.filter((_, i) => i !== index));
  }

  // --- Save / Publish ---
  function handleSaveDraft() {
    console.log('Save draft:', {
      name,
      overview,
      changeReason,
      safetyBoundaries,
      equipmentConfig,
      preChecks,
      postChecks,
      rollbackInstructions,
      uploadedFile: uploadedFile?.name ?? null,
    });
  }

  function handlePublish() {
    console.log('Publish:', {
      name,
      overview,
      changeReason,
      safetyBoundaries,
      equipmentConfig,
      preChecks,
      postChecks,
      rollbackInstructions,
      uploadedFile: uploadedFile?.name ?? null,
    });
  }

  // Compute next version label for edit mode
  const nextVersion = sop
    ? (() => {
        const parts = sop.version.replace('v', '').split('.');
        const major = parseInt(parts[0], 10);
        const minor = parseInt(parts[1], 10);
        return `v${major}.${minor + 1}`;
      })()
    : '';

  return (
    <div className="sop-editor-container">
      <Link
        to={sopId ? `/site/${site_id}/sop/${sopId}` : `/site/${site_id}/sop`}
        className="sop-back-link"
      >
        &larr; {isEditing ? 'Back to SOP' : 'All SOPs'}
      </Link>

      {/* Header Card */}
      <div className="sop-editor-card sop-editor-header-card">
        {isEditing && sop ? (
          <>
            <h2>EDIT: {sop.name}</h2>
            <p className="sop-editor-subtitle">
              Editing {sop.version} &rarr; will create {nextVersion}
            </p>
          </>
        ) : (
          <h2>New SOP</h2>
        )}
      </div>

      {/* Document Upload */}
      <div className="sop-editor-card">
        <h3 className="sop-editor-card-title">
          {isEditing ? 'Upload Revised Document' : 'Upload Document'}
        </h3>
        <p className="sop-editor-upload-description">
          {isEditing
            ? 'Upload a revised SOP document to replace the current version, or edit the fields below manually.'
            : 'Upload an existing SOP document to pre-populate fields, or fill out the form manually below.'}
        </p>

        {uploadedFile ? (
          <div className="sop-editor-uploaded-file">
            <div className="sop-editor-uploaded-file-info">
              <span className="sop-editor-file-icon">{'\uD83D\uDCC4'}</span>
              <div className="sop-editor-file-details">
                <span className="sop-editor-file-name">{uploadedFile.name}</span>
                <span className="sop-editor-file-size">{formatFileSize(uploadedFile.size)}</span>
              </div>
            </div>
            <button type="button" className="sop-editor-remove-file-btn" onClick={handleRemoveFile}>
              &times;
            </button>
          </div>
        ) : (
          <div
            className={`sop-editor-dropzone ${isDragOver ? 'sop-editor-dropzone-active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="sop-editor-dropzone-icon">{'\uD83D\uDCC1'}</div>
            <p className="sop-editor-dropzone-text">
              Drag and drop a document here, or{' '}
              <span className="sop-editor-browse-link">browse</span>
            </p>
            <p className="sop-editor-dropzone-hint">Supports PDF, DOCX, DOC, TXT, MD</p>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_EXTENSIONS}
              onChange={handleFileInputChange}
              className="sop-editor-file-input-hidden"
            />
          </div>
        )}

        {!uploadedFile && (
          <div className="sop-editor-upload-divider">
            <span className="sop-editor-upload-divider-text">or fill out manually below</span>
          </div>
        )}
      </div>

      {/* Change Reason (edit only) */}
      {isEditing && (
        <div className="sop-editor-card">
          <h3 className="sop-editor-card-title">Change Reason</h3>
          <textarea
            className="sop-editor-textarea"
            rows={3}
            value={changeReason}
            onChange={e => setChangeReason(e.target.value)}
            placeholder="Describe why this change is being made..."
          />
        </div>
      )}

      {/* Name */}
      <div className="sop-editor-card">
        <h3 className="sop-editor-card-title">Name</h3>
        <input
          type="text"
          className="sop-editor-input"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Procedure name..."
        />
      </div>

      {/* Overview */}
      <div className="sop-editor-card">
        <h3 className="sop-editor-card-title">Overview</h3>
        <textarea
          className="sop-editor-textarea"
          rows={5}
          value={overview}
          onChange={e => setOverview(e.target.value)}
          placeholder="Describe the procedure..."
        />
      </div>

      {/* Safety Boundaries */}
      <div className="sop-editor-card">
        <h3 className="sop-editor-card-title">Safety Boundaries</h3>
        <table className="sop-editor-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Min</th>
              <th>Max</th>
              <th>Unit</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {safetyBoundaries.map((row, i) => (
              <tr key={i}>
                <td>
                  <input
                    type="text"
                    className="sop-editor-cell-input"
                    value={row.parameter}
                    onChange={e => updateBoundaryRow(i, 'parameter', e.target.value)}
                    placeholder="Parameter"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="sop-editor-cell-input"
                    value={row.min}
                    onChange={e => updateBoundaryRow(i, 'min', e.target.value)}
                    placeholder="Min"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="sop-editor-cell-input"
                    value={row.max}
                    onChange={e => updateBoundaryRow(i, 'max', e.target.value)}
                    placeholder="Max"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="sop-editor-cell-input"
                    value={row.unit}
                    onChange={e => updateBoundaryRow(i, 'unit', e.target.value)}
                    placeholder="Unit"
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="sop-editor-remove-btn"
                    onClick={() => removeBoundaryRow(i)}
                  >
                    &times;
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" className="sop-editor-add-btn" onClick={addBoundaryRow}>
          + Add Row
        </button>
      </div>

      {/* Equipment Configuration */}
      <div className="sop-editor-card">
        <h3 className="sop-editor-card-title">Equipment Configuration</h3>
        <table className="sop-editor-table">
          <thead>
            <tr>
              <th>Equipment</th>
              <th>Parameter</th>
              <th>Normal</th>
              <th>Optimized</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {equipmentConfig.map((row, i) => (
              <tr key={i}>
                <td>
                  <input
                    type="text"
                    className="sop-editor-cell-input"
                    value={row.equipment}
                    onChange={e => updateEquipmentRow(i, 'equipment', e.target.value)}
                    placeholder="Equipment"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="sop-editor-cell-input"
                    value={row.parameter}
                    onChange={e => updateEquipmentRow(i, 'parameter', e.target.value)}
                    placeholder="Parameter"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="sop-editor-cell-input"
                    value={row.normal}
                    onChange={e => updateEquipmentRow(i, 'normal', e.target.value)}
                    placeholder="Normal"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    className="sop-editor-cell-input"
                    value={row.optimized}
                    onChange={e => updateEquipmentRow(i, 'optimized', e.target.value)}
                    placeholder="Optimized"
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="sop-editor-remove-btn"
                    onClick={() => removeEquipmentRow(i)}
                  >
                    &times;
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" className="sop-editor-add-btn" onClick={addEquipmentRow}>
          + Add Row
        </button>
      </div>

      {/* Pre-Execution Checks */}
      <div className="sop-editor-card">
        <h3 className="sop-editor-card-title">Pre-Execution Checks</h3>
        <ul className="sop-editor-check-list">
          {preChecks.map((check, i) => (
            <li key={i} className="sop-editor-check-item">
              <input
                type="text"
                className="sop-editor-check-input"
                value={check}
                onChange={e => updateCheck('pre', i, e.target.value)}
                placeholder="Check description..."
              />
              <button
                type="button"
                className="sop-editor-remove-btn"
                onClick={() => removeCheck('pre', i)}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="sop-editor-add-btn" onClick={() => addCheck('pre')}>
          + Add Check
        </button>
      </div>

      {/* Post-Execution Checks */}
      <div className="sop-editor-card">
        <h3 className="sop-editor-card-title">Post-Execution Checks</h3>
        <ul className="sop-editor-check-list">
          {postChecks.map((check, i) => (
            <li key={i} className="sop-editor-check-item">
              <input
                type="text"
                className="sop-editor-check-input"
                value={check}
                onChange={e => updateCheck('post', i, e.target.value)}
                placeholder="Check description..."
              />
              <button
                type="button"
                className="sop-editor-remove-btn"
                onClick={() => removeCheck('post', i)}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="sop-editor-add-btn" onClick={() => addCheck('post')}>
          + Add Check
        </button>
      </div>

      {/* Rollback Instructions */}
      <div className="sop-editor-card">
        <h3 className="sop-editor-card-title">Rollback Instructions</h3>
        <ol className="sop-editor-rollback-list">
          {rollbackInstructions.map((step, i) => (
            <li key={i} className="sop-editor-rollback-item">
              <span className="sop-editor-drag-handle">{'\u2261'}</span>
              <input
                type="text"
                className="sop-editor-check-input"
                value={step}
                onChange={e => updateRollbackStep(i, e.target.value)}
                placeholder="Rollback step..."
              />
              <button
                type="button"
                className="sop-editor-remove-btn"
                onClick={() => removeRollbackStep(i)}
              >
                &times;
              </button>
            </li>
          ))}
        </ol>
        <button type="button" className="sop-editor-add-btn" onClick={addRollbackStep}>
          + Add Step
        </button>
      </div>

      {/* Footer */}
      <div className="sop-editor-footer">
        <Link
          to={sopId ? `/site/${site_id}/sop/${sopId}` : `/site/${site_id}/sop`}
          className="sop-editor-btn sop-editor-btn-cancel"
        >
          Cancel
        </Link>
        <button
          type="button"
          className="sop-editor-btn sop-editor-btn-draft"
          onClick={handleSaveDraft}
        >
          Save Draft
        </button>
        <button
          type="button"
          className="sop-editor-btn sop-editor-btn-publish"
          onClick={handlePublish}
        >
          Publish
        </button>
      </div>
    </div>
  );
}

export default SOPEditor;
